import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { ForecastForReceiptRepository } from '@/coreInfrastructureModule/Repositories/ForecastForReceiptRepository';
import { IForecastForReceipt } from '@/coreDomainsModule/entities/forecast';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { FilterInfoState } from '@/corePagesModule/SelectInput/interface/uiState';
import { GetForecastForReceiptUseCase } from '../useCases/getForecastForReceiptUseCase';
import { ForecastForReceiptFormState } from '../domains/entities/ForecastForReceiptMenu';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';

/**
 * 受領用内示のリストのStoreを返す
 * @param forecastForReceiptTag - 受領用内示のタグ
 */
export const useForecastForReceiptListSelectInputStore = (forecastForReceiptTag: string) =>
  defineStore(`ForecastForReceiptListSelectInput-${forecastForReceiptTag}`, () => {
    const state = reactive<IForecastForReceiptList>({
      forecastForReceiptList: [],
      selectedForecastForReceiptId: undefined,
      selectedForecastForReceiptFormState: undefined,
      selectInputState: false,
      pagingState: { type: 'none' },
      pagingOptions: initPagingOptions(15),
    });

    /**
     * リセット
     */
    const $reset = () => {
      state.forecastForReceiptList = [];
      state.selectedForecastForReceiptId = undefined;
      state.selectedForecastForReceiptFormState = undefined;
      state.selectInputState = false;
      state.pagingState = { type: 'none' };
      state.pagingOptions = initPagingOptions(15);
    };

    /**
     * 初期化
     */
    const init = async () => {
      $reset();
      await fetchForecastForReceiptList();
    };

    // computed
    const ForecastForReceiptListNames = computed((): string[] =>
      state.forecastForReceiptList.map((forecastForReceipt) => forecastForReceipt.forecastForReceiptName)
    );

    const selectedForecastForReceipt = computed((): string => {
      return (
        state.forecastForReceiptList.find(
          (forecastForReceipt) => forecastForReceipt.forecastForReceiptId === state.selectedForecastForReceiptId
        )?.forecastForReceiptName ?? ''
      );
    });

    /**
     * 受領用内示データを再取得する。選択中の受領用内示がある場合はその受領用内示を選択した状態にして表字名を更新する
     */
    const reloadForecastForReceiptData = async () => {
      await fetchForecastForReceiptList();
      // 選択中の受領用内示がある場合はその受領用内示の選択状態は維持する
      _setSelectedForecastForReceiptId(state.selectedForecastForReceiptId, state.selectedForecastForReceiptFormState);
    };

    /**
     * ページング情報をリセットする
     */
    const resetSelectInputState = () => {
      state.pagingState = { type: 'none' };
      state.pagingOptions = initPagingOptions(15);
    };

    /**
     * 受領用内示リストを取得する
     */
    const fetchForecastForReceiptList = async () => {
      const offset = state.pagingOptions.nextOffset ?? state.pagingOptions.offset;

      if (offset === 0) {
        // ファーストローディング
        state.pagingState = {
          type: 'loading',
        };
      } else if (state.pagingState.type === 'success') {
        state.pagingState = {
          type: 'success',
          footerLoadingState: 'loading',
          data: state.pagingState.data,
        };
      }

      // オフセットを更新
      state.pagingOptions = {
        ...state.pagingOptions,
        offset: offset,
      };

      // リポジトリを取得
      const repository = new ForecastForReceiptRepository();
      const useCase = new GetForecastForReceiptUseCase();
      // APIを叩く
      const result = await useCase.Execute(repository);

      switch (result.status.type) {
        case 'success':
          // データをセット
          state.forecastForReceiptList = result.status.data.forecastForReceipts;
          state.pagingOptions = result.status.data.pagingOptions;
          if (state.forecastForReceiptList.length === 0) {
            state.pagingState = {
              type: 'zeroMatch',
            };
          } else {
            if (offset === 0) {
              // ファーストローディング
              state.pagingState = {
                type: 'success',
                footerLoadingState: 'append',
                data: state.forecastForReceiptList.map((forecastForReceipt) => forecastForReceipt.forecastForReceiptName),
              };
            } else if (state.pagingState.type === 'success') {
              // 追加ローディング
              state.pagingState = {
                type: 'success',
                footerLoadingState: 'append',
                data: state.pagingState.data.concat(
                  state.forecastForReceiptList.map((forecastForReceipt) => forecastForReceipt.forecastForReceiptName)
                ),
              };
            }
          }
          break;
        case 'error':
          if (offset === 0) {
            state.pagingState = {
              type: 'error',
              errorMessage: 'フィルター情報の取得に失敗しました', // TODO: APIからのエラーメッセージをハンドリング
            };
          } else if (state.pagingState.type === 'success') {
            state.pagingState = {
              type: 'success',
              footerLoadingState: 'error',
              data: state.pagingState.data,
            };
          }
          break;
      }
    };

    /**
     * 受領用内示が選択された際の処理
     * @param selectedIndex - 選択された受領用内示のインデックス
     */
    const onSelectedForecastForReceipt = (selectedIndex: number) => {
      // 選択された受領用内示を取得
      const selectedForecastForReceiptId = selectedIndex
        ? state.forecastForReceiptList[selectedIndex - 1].forecastForReceiptId
        : UNASSIGNED_COLUMN;
      const selectedForecastForReceiptFormState: ForecastForReceiptFormState = {
        forecastForReceiptName: selectedIndex ? state.forecastForReceiptList[selectedIndex - 1].forecastForReceiptName : '',
        forecastForReceiptResponsibleUserIdList: selectedIndex
          ? state.forecastForReceiptList[selectedIndex - 1].forecastForReceiptResponsibleUsers.map((user) => user.email ?? '')
          : [],
        outputRange: selectedIndex ? state.forecastForReceiptList[selectedIndex - 1].outputRange : 0,
      };
      _setSelectedForecastForReceiptId(selectedForecastForReceiptId, selectedForecastForReceiptFormState);
    };

    /**
     * 受領用内示の値が更新された際の処理
     */
    const onSelectedDefaultValue = () => {
      _setSelectedForecastForReceiptId(undefined, undefined);
    };

    // private
    /**
     * forecastForeReceiptIdのsetter
     * @param forecastForeReceiptId - 受領用内示ID
     * @param forecastForReceiptFromState - 受領用内示のフォーム情報
     */
    const _setSelectedForecastForReceiptId = (
      forecastForeReceiptId: string | undefined,
      forecastForReceiptFromState: ForecastForReceiptFormState | undefined
    ) => {
      state.selectedForecastForReceiptId = forecastForeReceiptId;
      state.selectedForecastForReceiptFormState = forecastForReceiptFromState;
    };

    return {
      state,
      $reset,
      init,
      reloadForecastForReceiptData,
      resetSelectInputState,
      fetch,
      onSelectedForecastForReceipt,
      onSelectedDefaultValue,
      ForecastForReceiptListNames,
      selectedForecastForReceipt,
    };
  });

/**
 * 受領用内示のリスト
 */
interface IForecastForReceiptList {
  /**
   * 受領用内示のリスト
   */
  forecastForReceiptList: IForecastForReceipt[];
  /**
   * 選択された受領用内示のID
   */
  selectedForecastForReceiptId?: string;
  /**
   * 選択された受領用内示のフォーム情報
   */
  selectedForecastForReceiptFormState: ForecastForReceiptFormState | undefined;
  /**
   * SelectInputの表示状態
   */
  selectInputState: boolean;
  /**
   * ページング情報
   */
  pagingOptions: IPagingOptions;
  /**
   * ページングの状態
   */
  pagingState: FilterInfoState;
}
